import { useState, useEffect } from 'react';

// Hook to detect online/offline status
export const useNetworkStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
};

// Utility function to check if an error is a network error
export const isNetworkError = (error) => {
  if (!error) return false;
  
  // Check for common network error patterns
  const errorMessage = error.message?.toLowerCase() || '';
  const errorString = String(error).toLowerCase();
  
  return (
    !navigator.onLine ||
    errorMessage.includes('network') ||
    errorMessage.includes('fetch') ||
    errorMessage.includes('connection') ||
    errorMessage.includes('timeout') ||
    errorMessage.includes('failed to fetch') ||
    errorString.includes('network') ||
    errorString.includes('fetch') ||
    errorString.includes('connection') ||
    error?.code === 'NETWORK_ERROR' ||
    error?.name === 'NetworkError' ||
    (error?.response?.status >= 500 && error?.response?.status < 600)
  );
};

// Utility function to check if an error is a database/API error
export const isDatabaseError = (error) => {
  if (!error) return false;
  
  const errorMessage = error.message?.toLowerCase() || '';
  const errorString = String(error).toLowerCase();
  
  return (
    errorMessage.includes('database') ||
    errorMessage.includes('supabase') ||
    errorMessage.includes('postgres') ||
    errorMessage.includes('query') ||
    errorMessage.includes('sql') ||
    errorString.includes('database') ||
    errorString.includes('supabase') ||
    (error?.code && error.code.startsWith('PGRST')) ||
    (error?.response?.status >= 400 && error?.response?.status < 500)
  );
};

// Utility function to get a user-friendly error message
export const getErrorMessage = (error) => {
  if (!error) return "Something went wrong. Please try again.";
  
  if (isNetworkError(error)) {
    return "Can't connect right now. Check your connection and try again.";
  }
  
  if (isDatabaseError(error)) {
    return "Unable to load data. Please try again in a moment.";
  }
  
  // Return a generic message for other errors
  return "Something went wrong. Please try again.";
};

